import './styles.scss';

import { Button, Col, Row } from 'react-bootstrap';
import { Datetime, createNotification } from '@/utils';
import { DispatcherDropdownField, DriverDropdownField, LeaderDropdownField } from '@/components/UserDropdown';
import { EnumDropdownField, getItemsFromEnum } from '@/components/EnumDropdown';
import GenericFileUpload, { GenericFile, GenericFileType } from '@/components/FileUpload/new';
import {
  IncidentIssueWithCacheCrewInvolvedEnum,
  IncidentIssueWithCacheIncidentOccuranceEnum,
  IncidentIssueWithCachePropertyDamageEnum,
} from '@/models/gen/graphql';
import { useMemo, useRef, useState } from 'react';

import { AirportDropdownField } from '@/components/AirportDropdown';
import { AssignVehicleDropdownField } from '@/components/VehicleDropdown';
import { DATETIME_FORMAT } from '@/constants';
import { DatetimeInputField } from '@/components/DatetimeInput';
import { FlightNumberOrLoopNameInputField } from '@/common/FlightNumberInput';
import Form from '@/common/Form';
import IncidentFormHistoryTable from '@/features/IncidentHistory/components/IncidentHistoryForm/IncidentFormHistoryTable';
import { IncidentHistoryFormDefinitions } from './types';
import { InputField } from '@/components/Input';
import { LoadingBlur } from '@/components/LoadingSpinner';
import { TextAreaField } from '@/components/TextArea';
import { Toast } from '@/models';
import { createIncidentIssue } from '@/features/IncidentHistory/api/services/createIncidentIssue';
import { createIncidentIssueDocumentPresignedUrl } from '@/features/IncidentHistory/api/services/createIncidentIssueDocumentPresignedUrlEndpoint';
import { deleteIncidentIssueDocument } from '../../api/services/deleteIncidentIssueDocument';
import { updateIncidentIssue } from '@/features/IncidentHistory/api/services/updateIncidentIssue';
import useConfirmation from '@/hooks/useConfirmation';
import { useIncidentHistory } from '@/features/IncidentHistory';
import { useIncidentHistoryDrawer } from '@/features/IncidentHistory/components/IncidentHistoryDrawer';
import useLocale from '@/hooks/useLocale';
import { useNavigate } from 'react-router-dom';
import DeferredFileUpload, { DeferredFileUploadHandle } from '@/components/FileUpload/deferred-file-upload';
import { recreateIncidentIssueDocumentDownloadUrl } from '@/features/IncidentHistory/api/services/createIncidentIssueDocumentDownloadURL';
import { useServiceIssueFilters } from '@/hooks/useServiceIssueFilters';

const IncidentHistoryForm = ({
  onSubmit,
  onEditIncident,
  locale: customLocale,
  value = {},
  trip,
}: IncidentHistoryFormDefinitions.Props): JSX.Element => {
  const [, setState, { submitFilters }] = useIncidentHistory();
  const setIncidentHistoryDrawer = useIncidentHistoryDrawer(({ setState }) => setState);
  const [documents, setDocuments] = useState(value.documents ?? []);
  const [loading, setLoading] = useState(false);
  const locale = useLocale({
    ...IncidentHistoryFormDefinitions.getLocale(value),
    ...customLocale,
  });
  const fileUploadRef = useRef<DeferredFileUploadHandle>(null);
  const confirmDocumentDelete = useConfirmation();
  const navigate = useNavigate();
  const handleDeleteDocument = (id: string) => {
    confirmDocumentDelete()
      .then(async () => {
        try {
          await deleteIncidentIssueDocument([{ id }]);
          createNotification('Sucessfully Deleted Document', Toast.Type.SUCCESS, 'Incident Issue');
          value.documents = value.documents.filter((document) => document.id !== id);
          setDocuments((current) => current.filter((document) => document.id !== id));
          await submitFilters?.(useServiceIssueFilters.getState().state); // Because we aren't resubmitting on closing the form
        } catch (e) {
          console.error('Error Deleting Document.', e);
          createNotification('Could Not Delete Document', Toast.Type.DANGER, 'Incident Issue');
        }
      })
      .catch(() => console.log('Cancelled deleting document'));
  };
  const handleSubmit = async (data) => {
    try {
      setLoading(true);
      if (data.id) {
        await updateIncidentIssue([data]);
        createNotification('Sucessfully Updated Incident', Toast.Type.SUCCESS, 'Incident Issue');
        setState((current) => ({
          ...current,
          incidents: current.incidents.set(data.id, {
            ...current.incidents.get(data.id),
            ...data,
          }),
        }));
      } else {
        const filesToUpload = fileUploadRef.current?.getFilesToUpload() ?? [];
        const { id, ...create } = data;
        const createRes = await createIncidentIssue([
          { ...create, documents: filesToUpload.map((file) => ({ ...file, issueId: '', fileLocation: '' })) },
        ]);
        createNotification('Sucessfully Created Incident', Toast.Type.SUCCESS, 'Incident Issue');
        await fileUploadRef.current?.performDeferredUploads(createRes?.[0]?.output?.document ?? []); // No bulk create, so we only get back one issue
      }
    } catch (err) {
      console.error('Error Creating Lost and Found.', err);
      createNotification('Could Not Create Incident', Toast.Type.DANGER, 'Incident Issue');
    } finally {
      await submitFilters?.(useServiceIssueFilters.getState().state);
      setLoading(false);
      onSubmit?.(data);
    }
  };
  const gotoTrips = () => {
    navigate('/trips');
    setIncidentHistoryDrawer((current) => ({
      ...current,
      show: false,
    }));
  };
  const fullValue = useMemo(
    () => ({
      ...IncidentHistoryFormDefinitions.initValue,
      ...value,
      ...IncidentHistoryFormDefinitions.loadForm(),
    }),
    []
  );

  return (
    <Form
      name="IncidentHistoryForm"
      className="IncidentHistoryForm"
      onSubmit={handleSubmit}
      value={fullValue}
      validator={IncidentHistoryFormDefinitions.validator(locale)}
      onChange={IncidentHistoryFormDefinitions.saveForm}
    >
      <LoadingBlur loading={loading} />
      <Form.Input type="hidden" name="id" />
      <Form.Input type="hidden" name="tripId" />
      <Form.Input type="hidden" name="station" />
      <Row className="IncidentHistoryFormHeader">
        <Col>
          {value.tripId && (
            <>
              <span>
                {value.tripServicerIataAirlineCode}
                {value.tripLoopName || value.tripFlightNumber.toString().padStart(4, '0')}{' '}
                {new Datetime(value.tripScheduled).format(DATETIME_FORMAT)}
              </span>
              <span>
                {value.station} - {[value.driverFirstName, value.driverLastName].join(' ')} - #{value.vehicleTrackingId}
              </span>
            </>
          )}
          <h4 className="IncidentHistoryFormTitle">{locale('Incident')}</h4>
        </Col>
      </Row>
      {!value.tripId && !value?.id && (
        <Row className="IncidentHistoryFormLinkTrips">
          <Col>
            <span>THIS IS NOT LINKED TO TRIP.</span>
            <span>
              WOULD YOU LIKE TO LINK THIS TO A TRIP?{' '}
              <Button variant="text" onClick={gotoTrips}>
                YES
              </Button>
            </span>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <Form.Input
            as={TextAreaField}
            name="description"
            label={locale('Description')}
            style={{ height: '7rem', resize: 'none' }}
            validate
          />
        </Col>
      </Row>
      {!value.tripId && (
        <Row>
          <Col xs={{ span: 6, offset: 3 }}>
            <Form.Input as={AirportDropdownField} name="station" label={locale('Station')} validate />
          </Col>
        </Row>
      )}
      <Form.Watch name="station">
        {([station]: [string]): JSX.Element => {
          return (
            <Row>
              <Col xs={6}>
                <Form.Input as={DriverDropdownField} name="driverId" airports={station} label={locale('Driver Name')} validate />
              </Col>
              <Col xs={6}>
                <Form.Input
                  as={AssignVehicleDropdownField}
                  name="vehicleId"
                  label={locale('Van Number')}
                  airportCode={station}
                  disabled={!station}
                  validate
                />
              </Col>
            </Row>
          );
        }}
      </Form.Watch>
      <Row>
        <Col xs={6}>
          <Form.Input as={LeaderDropdownField} name="managerId" label={locale('Manager On Duty')} />
        </Col>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="crewInvolved"
            label={locale('Were Crew Onboard?')}
            enum={getItemsFromEnum(IncidentIssueWithCacheCrewInvolvedEnum)}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={DispatcherDropdownField} name="dispatcherId" label={locale('Dispatcher Name')} validate />
        </Col>
        <Col xs={6}>
          <Form.Input as={DatetimeInputField} name="incidentDatetime" label={locale('Incident Date/Time')} validate />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={FlightNumberOrLoopNameInputField} name="outboundFlightName" label={locale('Recovery Flight #')} />
        </Col>
        <Col xs={6}>
          <Form.Input as={DatetimeInputField} name="outboundDatetime" label={locale('Recovery FLT Date/Time')} />
        </Col>
      </Row>
      <Form.Watch name="crewInvolved">
        {([wasCrewInvolved]: [IncidentIssueWithCacheCrewInvolvedEnum]): JSX.Element =>
          wasCrewInvolved === IncidentIssueWithCacheCrewInvolvedEnum.Yes && (
            <>
              <Row>
                <Col>
                  <Form.Input
                    as={TextAreaField}
                    name="crewInfo"
                    label={locale('Crew Info')}
                    placeholder={locale('Enter crew members name(s), phone(s), email(s)')}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Input as={TextAreaField} name="recoveryInfo" label={locale('Recovery Info')} />
                </Col>
              </Row>
            </>
          )
        }
      </Form.Watch>
      <Row>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="propertyDamage"
            label={locale('Property Damage?')}
            enum={getItemsFromEnum(IncidentIssueWithCachePropertyDamageEnum)}
            validate
          />
        </Col>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="incidentOccurance"
            label={locale('Where Did This Occur?')}
            enum={getItemsFromEnum(IncidentIssueWithCacheIncidentOccuranceEnum)}
            validate
          />
        </Col>
      </Row>
      <Form.Watch name="incidentOccurance">
        {([incidentOccurance]: [IncidentIssueWithCacheIncidentOccuranceEnum]): JSX.Element =>
          incidentOccurance === IncidentIssueWithCacheIncidentOccuranceEnum.Other && (
            <Row>
              <Col>
                <Form.Input as={InputField} name="incidentOccuranceOther" label={locale('Occurence Location?')} />
              </Col>
            </Row>
          )
        }
      </Form.Watch>
      <Row>
        <Col>
          <Form.Button name="SUBMIT" type="submit" variant={locale('SUBMIT_BUTTON_COLOR')}>
            {locale('SUBMIT_BUTTON_TEXT')}
          </Form.Button>
        </Col>
      </Row>
      {value.id && (
        <>
          <Row className="IncidentHistoryFormUpload">
            <Col>
              <GenericFileUpload
                endpoint={async (file: GenericFileType) => {
                  try {
                    const payload = {
                      filename: file.file.name,
                      md5: file.md5,
                      fileSize: file.file.size,
                      issueId: value.id,
                    };
                    const res = await createIncidentIssueDocumentPresignedUrl(payload);
                    createNotification('Sucessfully Uploaded Document', Toast.Type.SUCCESS, 'Incident Issue');
                    return [res?.url, res?.contentType];
                  } catch (e) {
                    console.error('Error Uploading Document', e);
                    createNotification('Could Not Upload Document', Toast.Type.DANGER, 'Incident Issue');
                  }
                }}
                options={{
                  showFiles: true,
                  multiple: true,
                }}
                locale={{
                  Upload: 'UPLOAD FILES',
                }}
                onError={async (...args) => console.log('', ...args)}
                onSuccess={async (...args) => {
                  await submitFilters?.(useServiceIssueFilters.getState().state);
                  const documents = await recreateIncidentIssueDocumentDownloadUrl({ issueId: [value.id] });
                  const res = documents.urls.map((document) => {
                    const md5Match = document.url.match(/incident\/([a-f0-9]{32})\//); // Extract md5 from URL
                    const md5 = md5Match ? md5Match[1] : null;
                    return {
                      md5,
                      url: document.url,
                    };
                  });
                  return res;
                }}
              />
            </Col>
          </Row>
          {documents?.map((document) => (
            <Row className="mb-2" key={document.id}>
              <Col>
                <GenericFile name={document.filename} preview={document.fileLocation} onDelete={() => handleDeleteDocument(document.id)} />
              </Col>
            </Row>
          ))}
        </>
      )}
      {!value.id && (
        <>
          <Row className="IncidentHistoryFormUpload">
            <Col>
              <DeferredFileUpload ref={fileUploadRef} options={{ multiple: true, showFiles: true }} />
            </Col>
          </Row>
        </>
      )}
      {value.tripId && (
        <>
          <Row className="IncidentHistoryFormHistory">
            <Col>
              <h4 className="IncidentHistoryFormHistoryTitle">{locale('Incident History')}</h4>
            </Col>
          </Row>
          <Row>
            <Col>
              <IncidentFormHistoryTable
                onEditIncident={onEditIncident}
                id={value.tripId}
                driverFirstName={trip?.driver?.firstName}
                driverLastName={trip?.driver?.lastName}
                vehicleTrackingId={trip?.vehicle?.trackingId}
              />
            </Col>
          </Row>
        </>
      )}
    </Form>
  );
};

export default IncidentHistoryForm;
